import './App.css';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = 'https://api.wheretheiss.at/v1/satellites/25544';
const INTERVALO_SEGUNDOS = 2;
const MAX_PONTOS_ROTA = 150;

const issIcon = new L.Icon({
  iconUrl: '/iss-icon.png',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
  popupAnchor: [0, -22],
});

const AtualizarMapa = ({ posicao, travar }) => {
  const map = useMap();
  useEffect(() => {
    if (travar) {
      map.setView(posicao, map.getZoom(), { animate: true });
    }
  }, [posicao, travar, map]);
  return null;
};

const formatarLat = (v) => `${Math.abs(v).toFixed(4)}° ${v >= 0 ? 'N' : 'S'}`;
const formatarLon = (v) => `${Math.abs(v).toFixed(4)}° ${v >= 0 ? 'L' : 'O'}`;

const VISIBILIDADE = {
  daylight: { texto: 'Luz do dia', icone: '☀️' },
  visible: { texto: 'Visível', icone: '👁️' },
  eclipsed: { texto: 'Em eclipse', icone: '🌑' },
};

const Card = ({ icone, rotulo, valor, unidade }) => (
  <div className="card">
    <span className="card-icone" aria-hidden="true">{icone}</span>
    <div>
      <p className="card-rotulo">{rotulo}</p>
      <p className="card-valor">
        {valor}
        {unidade && <small> {unidade}</small>}
      </p>
    </div>
  </div>
);

const App = () => {
  const [info, setInfo] = useState(null);
  const [rota, setRota] = useState([]);
  const [travar, setTravar] = useState(true);
  const [erro, setErro] = useState(false);
  const [restante, setRestante] = useState(INTERVALO_SEGUNDOS);

  const buscarDados = useCallback(async () => {
    try {
      const { data } = await axios.get(API_URL);
      setInfo(data);
      setErro(false);
      setRota((r) =>
        [...r, [data.latitude, data.longitude]].slice(-MAX_PONTOS_ROTA)
      );
    } catch (e) {
      console.error('Erro ao buscar dados:', e);
      setErro(true);
    }
  }, []);

  useEffect(() => {
    buscarDados();
    const id = setInterval(() => {
      setRestante((s) => {
        if (s <= 1) {
          buscarDados();
          return INTERVALO_SEGUNDOS;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [buscarDados]);

  const posicao = info ? [info.latitude, info.longitude] : null;
  const progresso = ((INTERVALO_SEGUNDOS - restante) / INTERVALO_SEGUNDOS) * 100;
  const vis = info && (VISIBILIDADE[info.visibility] || { texto: info.visibility, icone: '🛰️' });

  return (
    <div className="app">
      <header className="topo">
        <div className="marca">
          <img src="/iss-icon.png" alt="" className="marca-logo" />
          <div>
            <h1>Rastreador da ISS</h1>
            <p>Estação Espacial Internacional em tempo real</p>
          </div>
        </div>
        <span className={`status ${erro ? 'status-erro' : ''}`}>
          <i className="status-ponto" />
          {erro ? 'Sem conexão' : 'Ao vivo'}
        </span>
      </header>

      <div className="barra-container" aria-hidden="true">
        <div className="barra-progresso" style={{ width: `${progresso}%` }} />
      </div>

      <main className="conteudo">
        <section className="mapa-wrap">
          <div className="mapa-controles">
            <button
              className={`botao ${travar ? 'botao-ativo' : ''}`}
              onClick={() => setTravar((v) => !v)}
            >
              {travar ? '📍 Seguindo a ISS' : '🧭 Mapa livre'}
            </button>
          </div>

          {posicao ? (
            <MapContainer center={posicao} zoom={4} className="mapa" worldCopyJump>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; <a href='https://www.esri.com/en-us/arcgis/products/arcgis-online'>Esri</a>"
              />
              <Polyline
                positions={rota}
                pathOptions={{ color: '#00e0ff', weight: 3, opacity: 0.8, dashArray: '6 6' }}
              />
              <Marker position={posicao} icon={issIcon}>
                <Popup>
                  <strong>ISS</strong>
                  <br />
                  {formatarLat(posicao[0])}, {formatarLon(posicao[1])}
                </Popup>
              </Marker>
              <AtualizarMapa posicao={posicao} travar={travar} />
            </MapContainer>
          ) : (
            <div className="mapa mapa-carregando">
              {erro ? 'Não foi possível carregar os dados.' : 'Localizando a ISS…'}
            </div>
          )}
        </section>

        {info && (
          <section className="cards">
            <Card icone="🚀" rotulo="Altitude" valor={info.altitude.toFixed(2)} unidade="km" />
            <Card icone="⚡" rotulo="Velocidade" valor={Math.round(info.velocity).toLocaleString('pt-BR')} unidade="km/h" />
            <Card icone="🧭" rotulo="Latitude" valor={formatarLat(info.latitude)} />
            <Card icone="🌍" rotulo="Longitude" valor={formatarLon(info.longitude)} />
            <Card icone={vis.icone} rotulo="Visibilidade" valor={vis.texto} />
            <Card icone="🕒" rotulo="Última atualização" valor={new Date(info.timestamp * 1000).toLocaleTimeString('pt-BR')} />
          </section>
        )}
      </main>

      <footer className="rodape">
        <p>
          Desenvolvido por{' '}
          <a href="https://portfolio-aryb.vercel.app/" target="_blank" rel="noopener noreferrer">
            Igor Carlos Dias
          </a>
        </p>
        <p>
          Dados fornecidos por{' '}
          <a href="https://wheretheiss.at/" target="_blank" rel="noopener noreferrer">
            Where the ISS at?
          </a>
        </p>
      </footer>
    </div>
  );
};

export default App;
